type BadgingNavigator = Navigator & {
   setAppBadge?: (contents?: number) => Promise<void>
   clearAppBadge?: () => Promise<void>
}

type AndroidBadgeBridge = {
   setOpenHabitCount?: (count: number) => void
}

type BadgingWindow = Window & {
   AndroidBadge?: AndroidBadgeBridge
}

const APP_BADGE_COUNT_STORAGE_KEY = 'openHabitTaskBadgeCount'

/**
 * Returns the navigator typed with Badging API methods, or null when unavailable.
 *
 * @returns The badging navigator, or null.
 */
function getBadgingNavigator() {
   if (typeof navigator === 'undefined') {
      return null
   }
   return navigator as BadgingNavigator
}

/**
 * Updates the native Android app-badge count via the injected bridge, when present.
 *
 * @param count - The badge count to set.
 */
function updateNativeAndroidBadge(count: number) {
   if (typeof window === 'undefined') {
      return
   }

   const badgeBridge = (window as BadgingWindow).AndroidBadge
   if (typeof badgeBridge?.setOpenHabitCount !== 'function') {
      return
   }

   try {
      badgeBridge.setOpenHabitCount(count)
   } catch (error) {
      console.error('Failed to update native app badge', error)
   }
}

/**
 * Persists the last badge count requested by the app.
 *
 * @param count - The normalized badge count.
 */
function persistAppBadgeCount(count: number) {
   if (typeof window === 'undefined') {
      return
   }

   try {
      window.localStorage.setItem(APP_BADGE_COUNT_STORAGE_KEY, String(count))
   } catch (error) {
      console.error('Failed to persist app badge count', error)
   }
}

/**
 * Returns the last badge count requested by the app.
 *
 * @returns The stored badge count, or 0 when unavailable.
 */
export function getLastRequestedAppBadgeCount() {
   if (typeof window === 'undefined') {
      return 0
   }

   const parsed = Number(window.localStorage.getItem(APP_BADGE_COUNT_STORAGE_KEY))
   return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0
}

/**
 * Clears the web app badge, falling back to `setAppBadge(0)` for partial implementations.
 */
export function clearWebAppBadge() {
   const badgingNavigator = getBadgingNavigator()
   if (!badgingNavigator) {
      return
   }

   if (typeof badgingNavigator.clearAppBadge === 'function') {
      badgingNavigator.clearAppBadge().catch(error => {
         console.error('Failed to clear app badge', error)
      })
      return
   }

   if (typeof badgingNavigator.setAppBadge === 'function') {
      badgingNavigator.setAppBadge(0).catch(error => {
         console.error('Failed to clear app badge', error)
      })
   }
}

/**
 * Persists, mirrors to the native bridge, and writes the OS badge count.
 *
 * @param count - The open habit task count (0 clears the badge).
 */
export function updateAppBadge(count: number) {
   const normalizedCount = Math.max(0, Math.round(count))
   persistAppBadgeCount(normalizedCount)
   updateNativeAndroidBadge(normalizedCount)

   if (normalizedCount === 0) {
      clearWebAppBadge()
      return
   }

   const badgingNavigator = getBadgingNavigator()
   if (typeof badgingNavigator?.setAppBadge === 'function') {
      badgingNavigator.setAppBadge(normalizedCount).catch(error => {
         console.error('Failed to set app badge', error)
      })
   }
}
